import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { settingsAPI } from '../services/api';
import appConfig from '../config/appConfig';

const ThemeContext = createContext();

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

// Default branding values - uses appConfig for customization
const defaultBranding = {
  company_name: appConfig.companyName,
  logo: null,
  favicon: null,
  theme: {
    light: {
      primary: appConfig.primaryColor,
      secondary: appConfig.secondaryColor,
      background: '#f5f5f5',
      sidebar: '#1e293b',
    },
    dark: {
      primary: '#90caf9',
      secondary: '#f48fb1',
      background: '#121212',
      sidebar: '#0f172a',
    },
  },
  default_dark_mode: appConfig.defaultDarkMode,
};

export const ThemeProvider = ({ children }) => {
  const [branding, setBranding] = useState(defaultBranding);
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return false; // Will be updated when branding loads
  });
  const [loading, setLoading] = useState(true);

  // Fetch branding settings on mount
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await settingsAPI.getBranding();
        setBranding(response.data);
        
        // Set default dark mode if not already set by user
        const savedMode = localStorage.getItem('darkMode');
        if (savedMode === null && response.data.default_dark_mode) {
          setDarkMode(true);
        }
      } catch (error) {
        console.error('Failed to fetch branding settings:', error);
        // Use defaults on error
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, []);

  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Update favicon when branding changes
  useEffect(() => {
    if (branding.favicon) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = branding.favicon;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    
    // Update document title
    document.title = branding.company_name;
  }, [branding]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // Refresh branding settings (called when company settings are updated)
  const refreshBranding = async () => {
    try {
      const response = await settingsAPI.getBranding();
      setBranding(response.data);
      
      // Set default dark mode if not already set by user
      const savedMode = localStorage.getItem('darkMode');
      if (savedMode === null && response.data.default_dark_mode) {
        setDarkMode(true);
      }
    } catch (error) {
      console.error('Failed to refresh branding settings:', error);
    }
  };

  // Create MUI theme based on current mode and branding
  const theme = useMemo(() => {
    const colors = darkMode ? branding.theme.dark : branding.theme.light;
    
    return createTheme({
      palette: {
        mode: darkMode ? 'dark' : 'light',
        primary: {
          main: colors.primary,
        },
        secondary: {
          main: colors.secondary,
        },
        background: {
          default: colors.background,
          paper: darkMode ? '#1e1e1e' : '#ffffff',
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: colors.background,
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: colors.sidebar,
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: darkMode ? '#1e1e1e' : colors.primary,
            },
          },
        },
      },
    });
  }, [darkMode, branding]);

  const value = {
    darkMode,
    toggleDarkMode,
    branding,
    loading,
    sidebarColor: darkMode ? branding.theme.dark.sidebar : branding.theme.light.sidebar,
    refreshBranding,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
